import { computed } from 'vue'
import dayjs from 'dayjs'
import type { Schedule } from '@/types'
import { useScheduleStore } from '@/stores/scheduleStore'
import { DATE_FORMAT, describeShiftTiming, pluralViolations, useWallClockNow } from '@/components/schedule/weekSchedule'

// Ячейка табеля (RD-139): месяц и неделя показывают готовые значения private POST /api/timesheets/
// (timesheet.days[DD].totals, day_type_code, shifts[]) без собственного расчёта часов.

export interface TimesheetTotals {
    main_seconds?: number
    additional_seconds?: number
    fact_seconds?: number
    fact_night_seconds?: number
}

export interface TimesheetDayShift {
    id?: string
    position_name?: string
    plan_start?: string | null
    plan_end?: string | null
    fact_start?: string | null
    fact_end?: string | null
    fact_seconds?: number
    day_type_code?: string
    is_dayoff?: boolean
    is_vacancy?: boolean
    is_overtime?: boolean
    is_not_main_position?: boolean
    is_not_main_department?: boolean
}

export interface TimesheetDay {
    day_type_code?: string
    totals?: TimesheetTotals
    shifts?: TimesheetDayShift[]
}

// Известные коды табеля (spec RD-139): рабочий день, праздник, прогул. Остальные — справочник DayType,
// не закрытый enum: код из справочника — кадровое состояние, неизвестный код показывается как есть.
const WORK_DAY_CODE = 'WD'
const HOLIDAY_CODE = 'H'
const ABSENCE_CODE = 'ABS'

export type TimesheetCellMode = 'future' | 'dayoff' | 'work' | 'absence' | 'dayState' | 'code'

const isSeconds = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

// Ч:ММ без сброса суток: 168:00 и больше остаются часами. Нет значения — пустая строка, не ноль.
export function formatTimesheetSeconds(value: unknown) {
    if (!isSeconds(value)) return ''
    const sign = value < 0 ? '-' : ''
    const totalMinutes = Math.floor(Math.abs(value) / 60)
    return `${sign}${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`
}

// Время смены из табеля — как в прежней подсказке табеля: короткое «ЧЧ:ММ» как есть, иначе dayjs.
export function formatTimesheetShiftTime(value: string | null | undefined) {
    if (!value) return '??:??'
    if (/^\d{2}:\d{2}/.test(value)) return value.slice(0, 5)
    const time = dayjs(value)
    return time.isValid() ? time.format('HH:mm') : '??:??'
}

// Дни табеля — объект с ключами «01»…«31»; сравнение по числу на случай ключа без ведущего нуля.
function findDay(days: Record<string, TimesheetDay> | undefined, dayNumber: number) {
    if (!days) return undefined
    const key = Object.keys(days).find(item => Number(item) === dayNumber)
    return key === undefined ? undefined : days[key]
}

export function useTimesheetCell(props: { schedule: Schedule, date: string }) {
    const store = useScheduleStore()
    // Общий минутный тикер графика: классификация «Нет отметок»/нарушений как в ячейках графика.
    const now = useWallClockNow()

    const day = computed(() => dayjs(props.date))
    const isToday = computed(() => props.date === dayjs().format(DATE_FORMAT))
    const isFuture = computed(() => day.value.isAfter(dayjs(), 'day'))

    // Ключ данных: месяц + employment_id + DD; таблица рисуется только после успешной загрузки всех месяцев.
    const timesheetDay = computed<TimesheetDay | undefined>(() => {
        const item = store.getEmploymentTimesheet(props.schedule.id, props.date.slice(0, 7))
        return findDay(item?.timesheet?.days, day.value.date())
    })
    const totals = computed<TimesheetTotals>(() => timesheetDay.value?.totals ?? {})
    const code = computed(() => timesheetDay.value?.day_type_code ?? timesheetDay.value?.shifts?.[0]?.day_type_code ?? '')
    const findDayType = (value: string | undefined) => value ? store.dayTypes.find(item => item.code === value) : undefined
    const dayType = computed(() => findDayType(code.value))
    // КС — код из справочника DayType, кроме известных WD/H/ABS.
    const isDayStateCode = (value: string | undefined) => Boolean(value)
        && value !== WORK_DAY_CODE && value !== HOLIDAY_CODE && value !== ABSENCE_CODE
        && Boolean(findDayType(value))

    // КС дня (r2): код дня или код записи КС в shifts[]. Общий код дня (например, ABS неявки доп. смены)
    // не скрывает присутствующее КС.
    const dayShifts = computed(() => timesheetDay.value?.shifts ?? [])
    const dayStateCode = computed(() => isDayStateCode(code.value)
        ? code.value
        : dayShifts.value.find(shift => isDayStateCode(shift.day_type_code))?.day_type_code ?? '')
    const dayStateType = computed(() => findDayType(dayStateCode.value))

    // Смены графика этой даты — источник нарушений (как в ячейках графика).
    const scheduleShifts = computed(() => props.schedule.shifts.filter(shift => {
        if (!dayjs(shift.date).isSame(day.value, 'day')) return false
        if (props.schedule.department.id === store.currentDepartment?.id) return true
        return shift.department === store.currentDepartment?.id
    }))
    const mode = computed<TimesheetCellMode>(() => {
        if (isFuture.value) return 'future'
        // Нет дня после успешной загрузки — прежний «ВЫХ».
        if (!timesheetDay.value) return 'dayoff'
        // КС — по коду табеля и справочнику, без зависимости от кадрового документа в сменах графика;
        // имеет приоритет над кодом дня, чтобы неявка доп. смены не заменяла КС «Прогулом».
        if (dayStateCode.value) return 'dayState'
        if (code.value === ABSENCE_CODE) return 'absence'
        if (code.value === WORK_DAY_CODE) return 'work'
        return 'code'
    })

    // Код дня: short_name справочника, пустой — первые 3 символа name (как в RD-138); неизвестный код — сам код.
    const codeShortName = (value: string) => {
        const type = findDayType(value)
        if (!type) return value
        return type.short_name?.trim() || type.name.trim().slice(0, 3)
    }
    const shortName = computed(() => code.value ? codeShortName(code.value) : 'ВЫХ')
    const fullName = computed(() => {
        if (!code.value) return 'Выходной'
        return dayType.value?.name || code.value
    })

    // Кадровое состояние: DayType кода табеля; цвет справочника, пустой/невалидный — color-day-state-bg (RD-138).
    const dayStateColor = computed(() => {
        const color = dayStateType.value?.color?.trim()
        return color && CSS.supports('color', color) ? color : 'var(--color-day-state-bg)'
    })
    const dayStateName = computed(() => dayStateType.value?.name || dayStateCode.value)
    const dayStateShortName = computed(() => dayStateCode.value ? codeShortName(dayStateCode.value) : '')

    const hasAdditional = computed(() => isSeconds(totals.value.additional_seconds) && totals.value.additional_seconds > 0)
    const hasNight = computed(() => isSeconds(totals.value.fact_night_seconds) && totals.value.fact_night_seconds > 0)
    const hasFact = computed(() => isSeconds(totals.value.fact_seconds))
    const factText = computed(() => formatTimesheetSeconds(totals.value.fact_seconds))
    const mainText = computed(() => formatTimesheetSeconds(totals.value.main_seconds))
    const additionalText = computed(() => hasAdditional.value ? `+${formatTimesheetSeconds(totals.value.additional_seconds)}` : '')
    const nightText = computed(() => hasNight.value ? formatTimesheetSeconds(totals.value.fact_night_seconds) : '')

    // Часы КС — сумма готовых fact_seconds его записей; длительность по времени не вычисляется.
    const dayStateHoursText = computed(() => {
        const values = dayShifts.value
            .filter(shift => shift.day_type_code === dayStateCode.value)
            .map(shift => shift.fact_seconds)
            .filter(isSeconds)
        return values.length ? formatTimesheetSeconds(values.reduce((sum, value) => sum + value, 0)) : ''
    })

    // Доп. смена в дне КС: флаги записи табеля или смены графика с тем же id (store.isExtraShift);
    // additional_seconds=0 не означает её отсутствия. Вакансии в табель не входят.
    const extraShifts = computed(() => {
        if (mode.value !== 'dayState') return []
        return dayShifts.value.filter(shift => {
            if (isDayStateCode(shift.day_type_code) || shift.is_dayoff || shift.is_vacancy) return false
            const scheduleShift = shift.id ? props.schedule.shifts.find(item => item.id === shift.id) : undefined
            return Boolean(store.isExtraShift(shift) || (scheduleShift && store.isExtraShift(scheduleShift)))
        })
    })
    // Подтверждённая неявка — собственный код записи ABS, не отсутствие отметок и не общий код дня.
    const extraAbsenceShift = computed(() => extraShifts.value.find(shift => shift.day_type_code === ABSENCE_CODE))
    // Строка «доп.» недельной ячейки КС: плашка положительных часов, иначе код неявки с её готовыми часами.
    const dayStateExtra = computed<{ kind: 'additional' | 'absence' | 'plain', code: string, text: string } | null>(() => {
        if (mode.value !== 'dayState' || (!extraShifts.value.length && !hasAdditional.value)) return null
        if (hasAdditional.value) return { kind: 'additional', code: '', text: additionalText.value }
        const absence = extraAbsenceShift.value
        // Нулевые часы неявки не выводятся (решение Романа 19.10.2026): «доп. ПРП» вместо «доп. ПРП · 0:00».
        if (absence) {
            const seconds = isSeconds(absence.fact_seconds) && absence.fact_seconds > 0 ? absence.fact_seconds : undefined
            return { kind: 'absence', code: codeShortName(ABSENCE_CODE), text: formatTimesheetSeconds(seconds) }
        }
        return { kind: 'plain', code: '', text: formatTimesheetSeconds(totals.value.additional_seconds) }
    })

    // Строка смены подсказки не нужна записи КС без валидного диапазона (??:??, Invalid Date, пустой интервал).
    const isDayStateShift = (shift: TimesheetDayShift) => isDayStateCode(shift.day_type_code)

    // Нарушения — статусы violation графика (moderate/warning) и тексты backend, общая классификация describeShiftTiming.
    const isViolationDataLoaded = computed(() => store.isScheduleContextLoaded)
    const shiftTimings = computed(() => isViolationDataLoaded.value
        ? scheduleShifts.value
            .filter(shift => !shift.day_state_document && shift.plan_work_start_at && shift.plan_work_end_at)
            .map(shift => describeShiftTiming(shift, now.value, undefined))
        : [])
    const violationCount = computed(() => shiftTimings.value.reduce((sum, timing) => sum + timing.violationCount, 0))
    const hasViolation = computed(() => violationCount.value > 0 || shiftTimings.value.some(timing => timing.hasGeneralViolation))
    const violationMessages = computed(() => shiftTimings.value.flatMap(timing => timing.violationMessages))
    // Чип недели — правило графика: одно нарушение — текст backend (нет текста — «Нарушение»), больше — «N нарушения».
    const violationChip = computed(() => {
        if (!hasViolation.value) return ''
        if (violationCount.value > 1) return pluralViolations(violationCount.value)
        return violationMessages.value[0] || 'Нарушение'
    })
    // Короткий чип для дня КС + доп. (решение Романа 19.10.2026): без текста backend, чтобы не обрезать название КС.
    const violationShortChip = computed(() => {
        if (!hasViolation.value) return ''
        return violationCount.value > 1 ? pluralViolations(violationCount.value) : 'Нарушение'
    })

    // Полосы не зависят от extended_cell_info; прогул (ABS) приоритетнее общей полосы нарушения.
    const stripe = computed<'warning' | 'no-marks' | undefined>(() => {
        if (mode.value === 'future') return undefined
        if (mode.value === 'absence' || extraAbsenceShift.value) return 'no-marks'
        if (hasViolation.value) return 'warning'
        return undefined
    })

    return {
        store,
        day,
        isToday,
        mode,
        timesheetDay,
        totals,
        code,
        shortName,
        fullName,
        dayStateName,
        dayStateShortName,
        dayStateColor,
        dayStateHoursText,
        dayStateExtra,
        isDayStateShift,
        hasAdditional,
        hasNight,
        hasFact,
        factText,
        mainText,
        additionalText,
        nightText,
        isViolationDataLoaded,
        shiftTimings,
        hasViolation,
        violationMessages,
        violationChip,
        violationShortChip,
        stripe,
    }
}
